import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  collection, onSnapshot, doc,
  updateDoc, deleteDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import './MembersPage.css'; // reuse existing table + dialog styles

const ALL = 'All';

const _months = ['Jan','Feb','Mar','Apr','May','Jun',
                 'Jul','Aug','Sep','Oct','Nov','Dec'];

function fmtDate(ts) {
  if (!ts?.toDate) return '—';
  const d = ts.toDate();
  return `${d.getDate()} ${_months[d.getMonth()]} ${d.getFullYear()}`;
}

function statusInfo(coupon) {
  if (!coupon.isActive) return { label: 'Inactive', color: '#9A9A9A' };
  const validUntil = coupon.validUntil?.toDate?.();
  if (!validUntil) return { label: 'Active', color: '#22C55E' };
  const days = Math.ceil((validUntil - new Date()) / 86400000);
  if (days < 0)  return { label: 'Expired',   color: '#E63946' };
  if (days <= 7) return { label: `${days}d left`, color: '#E63946' };
  if (days <= 30) return { label: `${days}d left`, color: '#D4B574' };
  return { label: 'Active', color: '#22C55E' };
}

export default function CouponsPage() {
  const [coupons, setCoupons]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search,  setSearch]    = useState('');
  const [status,  setStatus]    = useState(ALL);
  const [toast,   setToast]     = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'coupons'),
      (snap) => {
        setCoupons(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      () => setLoading(false),
    );
    return unsub;
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return coupons.filter(c => {
      const matchSearch = !q
        || c.title?.toLowerCase().includes(q)
        || c.partner?.toLowerCase().includes(q)
        || c.code?.toLowerCase().includes(q);
      const matchStatus = status === ALL
        || (status === 'Active'   &&  c.isActive)
        || (status === 'Inactive' && !c.isActive);
      return matchSearch && matchStatus;
    });
  }, [coupons, search, status]);

  const toggleActive = useCallback(async (coupon) => {
    try {
      await updateDoc(doc(db, 'coupons', coupon.id), {
        isActive:   !coupon.isActive,
        updated_at: serverTimestamp(),
      });
      showToast(
        `${coupon.title} ${!coupon.isActive ? 'activated' : 'deactivated'}.`,
        'success'
      );
    } catch (e) {
      showToast(`Failed: ${e.message}`, 'error');
    }
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await deleteDoc(doc(db, 'coupons', deleteTarget.id));
      showToast(`${deleteTarget.title} removed.`, 'success');
    } catch (e) {
      showToast(`Delete failed: ${e.message}`, 'error');
    } finally {
      setDeleteTarget(null);
    }
  }, [deleteTarget]);

  const showToast = (message, type) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Coupons</h1>
          <p className="page-subtitle">
            {coupons.length} total ·{' '}
            {coupons.filter(c => c.isActive).length} active
          </p>
        </div>
        <Link to="/coupons/add" className="mem-add-btn">+ Add Coupon</Link>
      </div>

      {toast && (
        <div className={`mem-toast mem-toast--${toast.type}`}>
          {toast.message}
        </div>
      )}

      <div className="mem-filters">
        <input
          type="search"
          className="mem-search"
          placeholder="Search title, partner, code…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          className="mem-select"
          value={status}
          onChange={e => setStatus(e.target.value)}
        >
          {[ALL, 'Active', 'Inactive'].map(s => <option key={s}>{s}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="mem-loading"><span className="tf-spinner tf-spinner--red" /></div>
      ) : filtered.length === 0 ? (
        <div className="mem-empty">No coupons match this filter.</div>
      ) : (
        <div className="mem-table-wrap">
          <table className="mem-table">
            <thead>
              <tr>
                <th>Coupon</th>
                <th>Partner</th>
                <th>Code</th>
                <th>Discount</th>
                <th>Valid Until</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => {
                const s = statusInfo(c);
                return (
                  <tr key={c.id}>
                    <td>
                      <div className="mem-name-cell">
                        {c.imageUrl ? (
                          <img
                            src={c.imageUrl}
                            alt=""
                            className="mem-avatar"
                            style={{ objectFit: 'contain', background: '#000' }}
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="mem-avatar">%</div>
                        )}
                        <div>
                          <div className="mem-name">{c.title}</div>
                          <div className="mem-phone">{c.category || '—'}</div>
                        </div>
                      </div>
                    </td>
                    <td>{c.partner}</td>
                    <td><code className="mem-id">{c.code}</code></td>
                    <td style={{ color: '#D4B574', fontWeight: 700 }}>{c.discount}</td>
                    <td style={{ fontSize: 12 }}>{fmtDate(c.validUntil)}</td>
                    <td style={{ color: s.color, fontWeight: 700, fontSize: 12 }}>
                      {s.label}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          onClick={() => toggleActive(c)}
                          title={c.isActive ? 'Deactivate' : 'Activate'}
                          style={{
                            padding: '6px 10px',
                            background: 'transparent',
                            border: `1px solid ${c.isActive ? '#9A9A9A' : '#22C55E'}`,
                            borderRadius: 6,
                            color: c.isActive ? '#9A9A9A' : '#22C55E',
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          {c.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          className="mem-delete-btn"
                          onClick={() => setDeleteTarget(c)}
                          title="Delete coupon"
                        >
                          ✕
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {deleteTarget && (
        <div className="mem-dialog-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="mem-dialog" onClick={e => e.stopPropagation()}>
            <h3 className="mem-dialog-title">Delete Coupon?</h3>
            <p className="mem-dialog-body">
              <strong>{deleteTarget.title}</strong> will be permanently removed.
              Members who already noted the code can still use it with the
              partner, but it will disappear from the app.
            </p>
            <div className="mem-dialog-actions">
              <button className="mem-dialog-cancel"
                onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="mem-dialog-confirm"
                onClick={confirmDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
